// NotificationsCard.tsx — bot-notifications design (docs/superpowers/specs/
// 2026-08-21-bot-notifications-design.md §Dashboard), Task 5. Mounted on `BotSettings.tsx`
// above the Danger zone, anchor `#notifications`. A matrix of Member x
// {New lead, Unanswered digest, Crawl failed, Weekly summary} — Switches for the three boolean
// events, a Select for the cadence one. `GET /api/bots/:botId/notifications` already returns only
// the rows the caller may edit (owner/admin: everyone; member: just their own row, Task 4's M1
// fix) — so unlike `Members.tsx`'s role-gated Select/Remove controls, EVERY row this screen
// receives is editable; there is no client-side role branch here at all.
import { useState } from 'react';
import type { BotNotificationMemberDto, DigestCadence, MemberRole, NotificationPrefsDto } from '@repo/shared-types';
import { DIGEST_CADENCES } from '@repo/shared-types';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/select';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { Switch } from '@repo/ui/components/switch';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@repo/ui/components/table';
import { useBotNotifications, usePatchNotificationPref } from '../hooks/useBotNotifications';
import { useT } from '../locales/context';
import type { Catalog } from '../locales/en';

function roleLabel(t: Catalog, role: MemberRole): string {
  return { owner: t.notifications.roleOwner, admin: t.notifications.roleAdmin, member: t.notifications.roleMember }[role];
}

function cadenceLabel(t: Catalog, cadence: DigestCadence): string {
  return { off: t.notifications.cadenceOff, daily: t.notifications.cadenceDaily, weekly: t.notifications.cadenceWeekly }[cadence];
}

/** Plain muted span, same idiom as `Members.tsx`'s own `RoleChip` (role isn't a status, so no
 * `StatusChip`) — not imported from there since that component isn't exported. */
function RoleChip({ t, role }: { t: Catalog; role: MemberRole }) {
  return (
    <span className="inline-flex w-fit items-center rounded-full bg-muted px-2.5 py-[3px] text-xs font-medium text-muted-foreground">
      {roleLabel(t, role)}
    </span>
  );
}

/** Sized to a 34px header plus a couple of 40px rows — same shape as `Members.tsx`'s
 * `MembersSkeleton`, just one table instead of two. */
function NotificationsSkeleton() {
  return (
    <div className="flex flex-col overflow-hidden rounded-lg border">
      <div className="h-[34px] bg-muted" />
      {[0, 1].map((i) => (
        <div key={i} className={`flex h-10 items-center gap-6 px-3 ${i === 0 ? '' : 'border-t'}`}>
          <SkeletonText className="w-40" />
          <Skeleton className="h-5 w-8 rounded-full" />
          <Skeleton className="h-8 w-24 rounded-md" />
          <Skeleton className="h-5 w-8 rounded-full" />
          <Skeleton className="h-5 w-8 rounded-full" />
        </div>
      ))}
    </div>
  );
}

type PrefField = keyof NotificationPrefsDto;

function cellKey(userId: string, field: PrefField) {
  return `${userId}:${field}`;
}

function MemberRow({
  t,
  member,
  cellErrors,
  onPatch,
}: {
  t: Catalog;
  member: BotNotificationMemberDto;
  cellErrors: Record<string, string>;
  onPatch: (userId: string, field: PrefField, value: boolean | DigestCadence) => void;
}) {
  const { userId, email, name, role, prefs } = member;

  return (
    <TableRow>
      <TableCell>
        <div className="flex flex-col gap-1">
          <div className="flex items-center gap-2">
            <span className="font-medium">{name ?? email}</span>
            <RoleChip t={t} role={role} />
          </div>
          {name && <span className="text-xs text-muted-foreground">{email}</span>}
        </div>
      </TableCell>
      <TableCell>
        <Switch
          checked={prefs.newLead}
          aria-label={t.notifications.newLeadAriaLabel({ email })}
          onCheckedChange={(value) => onPatch(userId, 'newLead', value)}
        />
        {cellErrors[cellKey(userId, 'newLead')] && (
          <p className="mt-1 whitespace-normal text-xs text-destructive">{t.notifications.saveError}</p>
        )}
      </TableCell>
      <TableCell>
        <Select
          value={prefs.unansweredDigest}
          onValueChange={(value) => onPatch(userId, 'unansweredDigest', value as DigestCadence)}
        >
          <SelectTrigger
            aria-label={t.notifications.unansweredDigestAriaLabel({ email })}
            size="sm"
            className="w-28"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DIGEST_CADENCES.map((cadence) => (
              <SelectItem key={cadence} value={cadence}>
                {cadenceLabel(t, cadence)}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        {cellErrors[cellKey(userId, 'unansweredDigest')] && (
          <p className="mt-1 whitespace-normal text-xs text-destructive">{t.notifications.saveError}</p>
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={prefs.crawlFailed}
          aria-label={t.notifications.crawlFailedAriaLabel({ email })}
          onCheckedChange={(value) => onPatch(userId, 'crawlFailed', value)}
        />
        {cellErrors[cellKey(userId, 'crawlFailed')] && (
          <p className="mt-1 whitespace-normal text-xs text-destructive">{t.notifications.saveError}</p>
        )}
      </TableCell>
      <TableCell>
        <Switch
          checked={prefs.weeklySummary}
          aria-label={t.notifications.weeklySummaryAriaLabel({ email })}
          onCheckedChange={(value) => onPatch(userId, 'weeklySummary', value)}
        />
        {cellErrors[cellKey(userId, 'weeklySummary')] && (
          <p className="mt-1 whitespace-normal text-xs text-destructive">{t.notifications.saveError}</p>
        )}
      </TableCell>
    </TableRow>
  );
}

// Locale-dashboard plan, Task 11 — resolved via `useT()` internally (documented on the group's
// own comment in en.ts): this component's dedicated test file renders it with no
// `LocaleProvider`, and `useT()`'s no-provider default (real English) is exactly what keeps
// that test green untouched.
export function NotificationsCard({ botId }: { botId: string | undefined }) {
  const t = useT();
  const { data, isPending, error } = useBotNotifications(botId);
  const patchPref = usePatchNotificationPref(botId);
  const [cellErrors, setCellErrors] = useState<Record<string, string>>({});

  function onPatch(userId: string, field: PrefField, value: boolean | DigestCadence) {
    const key = cellKey(userId, field);
    setCellErrors((prev) => {
      if (!(key in prev)) return prev;
      const next = { ...prev };
      delete next[key];
      return next;
    });
    patchPref.mutate(
      { userId, patch: { [field]: value } as Partial<NotificationPrefsDto> },
      { onError: () => setCellErrors((prev) => ({ ...prev, [key]: t.notifications.saveError })) },
    );
  }

  return (
    <section id="notifications" className="flex flex-col gap-3 rounded-xl border p-4">
      <div className="flex flex-col gap-1">
        <h2 className="text-sm font-semibold">{t.notifications.heading}</h2>
        <p className="text-sm text-muted-foreground">{t.notifications.subheading}</p>
      </div>

      {isPending && <NotificationsSkeleton />}
      {error && <p className="text-sm text-destructive">{error.message}</p>}

      {data && (
        <div className="overflow-x-auto rounded-lg border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{t.notifications.colMember}</TableHead>
                <TableHead>{t.notifications.colNewLead}</TableHead>
                <TableHead>{t.notifications.colUnansweredDigest}</TableHead>
                <TableHead>{t.notifications.colCrawlFailed}</TableHead>
                <TableHead>{t.notifications.colWeeklySummary}</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {/* Defensive only — the owner always exists, so this row is never expected to
                  actually render (Task 5 brief). */}
              {data.members.length === 0 && (
                <TableRow>
                  <TableCell colSpan={5} className="whitespace-normal text-center text-sm text-muted-foreground">
                    {t.notifications.noMembers}
                  </TableCell>
                </TableRow>
              )}
              {data.members.map((member) => (
                <MemberRow key={member.userId} t={t} member={member} cellErrors={cellErrors} onPatch={onPatch} />
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </section>
  );
}
